import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { getPlannedMetrics, getActualMetrics } from '../../../services/planningFinance';
import { getProjectDisplayName } from '../../../utils/planningDisplay';

const ProjectForm = ({ project, property, properties, onSave, onCancel }) => {
  const { formatCurrency } = useCurrency();
  const [selectedPropertyId, setSelectedPropertyId] = useState(property?.id || (properties?.[0]?.id || null));
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_type: 'renovation',
    estimated_cost: '',
    actual_cost: '',
    value_increase_percent: '',
    rent_increase_percent: '',
    planned_start_date: '',
    planned_end_date: '',
    actual_start_date: '',
    actual_end_date: '',
    status: 'planned',
    completion_notes: ''
  });

  const [plannedMetrics, setPlannedMetrics] = useState(null);
  const [actualMetrics, setActualMetrics] = useState(null);

  useEffect(() => {
    if (project) {
      setSelectedPropertyId(project?.propertyId || property?.id || (properties?.[0]?.id || null));
      setFormData({
        name: project?.name || '',
        description: project?.description || '',
        project_type: project?.project_type || 'renovation',
        estimated_cost: project?.estimated_cost?.toString() || '',
        actual_cost: project?.actual_cost?.toString() || '',
        value_increase_percent: project?.value_increase_percent?.toString() || '',
        rent_increase_percent: project?.rent_increase_percent?.toString() || '',
        planned_start_date: project?.planned_start_date || '',
        planned_end_date: project?.planned_end_date || '',
        actual_start_date: project?.actual_start_date || '',
        actual_end_date: project?.actual_end_date || '',
        status: project?.status || 'planned',
        completion_notes: project?.completion_notes || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        project_type: 'renovation',
        estimated_cost: '',
        actual_cost: '',
        value_increase_percent: '',
        rent_increase_percent: '',
        planned_start_date: '',
        planned_end_date: '',
        actual_start_date: '',
        actual_end_date: '',
        status: 'planned',
        completion_notes: ''
      });
    }
  }, [project, property, properties]);

  // Get the currently selected property
  const currentProperty = properties?.find(p => p?.id === selectedPropertyId) || property;

  // Calculate planning metrics when relevant fields change
  useEffect(() => {
    if (currentProperty && (formData?.estimated_cost || formData?.value_increase_percent || formData?.rent_increase_percent)) {
      const m = getPlannedMetrics(formData, currentProperty);
      setPlannedMetrics(m);
    } else {
      setPlannedMetrics(null);
    }

    if (currentProperty && formData?.actual_cost) {
      const m = getActualMetrics({ ...formData }, currentProperty);
      setActualMetrics(m);
    } else {
      setActualMetrics(null);
    }
  }, [formData?.estimated_cost, formData?.actual_cost, formData?.value_increase_percent, formData?.rent_increase_percent, currentProperty, selectedPropertyId]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, propertyId: selectedPropertyId });
  };

  const projectTypeOptions = [
    { value: 'renovation', label: 'Rénovation' },
    { value: 'expansion', label: 'Agrandissement' },
    { value: 'maintenance', label: 'Entretien' },
    { value: 'upgrade', label: 'Amélioration' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'legal', label: 'Juridique' },
    { value: 'other', label: 'Autre' }
  ];

  // Determine which fields to show based on project type
  const getProjectTypeFields = (projectType) => {
    const fields = {
      showValueIncrease: false,
      showRentIncrease: false,
      budgetLabel: 'Budget',
      budgetDescription: 'Coût total estimé du projet'
    };

    switch (projectType) {
      case 'renovation':
        fields.showValueIncrease = true;
        fields.showRentIncrease = true;
        fields.budgetLabel = 'Budget de rénovation';
        fields.budgetDescription = 'Coût total estimé de la rénovation';
        break;
      case 'expansion':
        fields.showValueIncrease = true;
        fields.showRentIncrease = true;
        fields.budgetLabel = 'Budget d\'agrandissement';
        fields.budgetDescription = 'Coût total estimé de l\'agrandissement';
        break;
      case 'upgrade':
        fields.showValueIncrease = true;
        fields.showRentIncrease = true;
        fields.budgetLabel = 'Budget d\'amélioration';
        fields.budgetDescription = 'Coût total estimé de l\'amélioration';
        break;
      case 'maintenance':
        fields.showValueIncrease = false;
        fields.showRentIncrease = false;
        fields.budgetLabel = 'Budget d\'entretien';
        fields.budgetDescription = 'Coût total estimé de l\'entretien';
        break;
      case 'marketing':
        fields.showValueIncrease = false;
        fields.showRentIncrease = false;
        fields.budgetLabel = 'Budget marketing';
        fields.budgetDescription = 'Coût total estimé des dépenses marketing';
        break;
      case 'legal':
        fields.showValueIncrease = false;
        fields.showRentIncrease = false;
        fields.budgetLabel = 'Budget juridique';
        fields.budgetDescription = 'Coût total estimé des frais juridiques';
        break;
      case 'other':
      default:
        fields.showValueIncrease = true;
        fields.showRentIncrease = true;
        fields.budgetLabel = 'Budget du projet';
        fields.budgetDescription = 'Coût total estimé du projet';
        break;
    }

    return fields;
  };

  const projectFields = getProjectTypeFields(formData.project_type);

  const statusOptions = [
    { value: 'planned', label: 'Planifié' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'on_hold', label: 'En attente' },
    { value: 'completed', label: 'Terminé' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-5 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {project ? 'Modifier le projet' : 'Nouveau projet'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onCancel}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-5 md:p-7 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Informations générales</h3>

            {/* Property Selector */}
            {properties && properties.length > 0 && (
              <Select
                label="Propriété"
                options={properties.map(p => ({
                  value: p.id,
                  label: `${p.address}${p.city ? ', ' + p.city : ''}`
                }))}
                value={selectedPropertyId}
                onChange={(value) => setSelectedPropertyId(value)}
                required
                description="Sélectionnez la propriété pour ce projet"
              />
            )}

            <Input
              label="Nom du projet"
              placeholder="Ex: Rénovation de la cuisine"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
              description={currentProperty ? getProjectDisplayName({ name: formData.name }, currentProperty) : undefined}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full min-h-[100px] px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Décrivez le projet..."
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Type de projet"
                options={projectTypeOptions}
                value={formData.project_type}
                onChange={(value) => handleFieldChange('project_type', value)}
                required
              />

              <Select
                label="Statut"
                options={statusOptions}
                value={formData.status}
                onChange={(value) => handleFieldChange('status', value)}
                required
              />
            </div>
          </div>

          {/* Financial Planning */}
          <div className="space-y-4 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Planification financière</h3>

            <Input
              label={projectFields.budgetLabel}
              type="number"
              placeholder="Ex: 50000"
              value={formData.estimated_cost}
              onChange={(e) => handleFieldChange('estimated_cost', e.target.value)}
              required
              min="0"
              description={projectFields.budgetDescription}
            />

            {projectFields.showValueIncrease && (
              <div className={projectFields.showRentIncrease ? "grid grid-cols-1 md:grid-cols-2 gap-4" : ""}>
                <Input
                  label="Gain en valeur (%)"
                  type="number"
                  placeholder="Ex: 15"
                  value={formData.value_increase_percent}
                  onChange={(e) => handleFieldChange('value_increase_percent', e.target.value)}
                  min="0"
                  max="100"
                  step="0.1"
                  description="Pourcentage d'augmentation de la valeur de la propriété"
                />

                {projectFields.showRentIncrease && (
                  <Input
                    label="Augmentation des revenus (%)"
                    type="number"
                    placeholder="Ex: 10"
                    value={formData.rent_increase_percent}
                    onChange={(e) => handleFieldChange('rent_increase_percent', e.target.value)}
                    min="0"
                    max="100"
                    step="0.1"
                    description="Pourcentage d'augmentation des revenus locatifs"
                  />
                )}
              </div>
            )}

            {/* Planning metrics - Planned (value creation, net cost, recovery rate, real yield) */}
            {plannedMetrics && projectFields.showValueIncrease && (plannedMetrics.valueCreation > 0 || plannedMetrics.additionalAnnualIncome > 0) && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Impact du projet (planifié)</h4>
                <div className={`grid gap-4 ${projectFields.showRentIncrease ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Valeur créée</p>
                    <p className="text-lg font-semibold text-foreground">{formatCurrency(plannedMetrics.valueCreation)}</p>
                  </div>
                  {projectFields.showRentIncrease && plannedMetrics.additionalAnnualIncome > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Revenus additionnels annuels</p>
                      <p className="text-lg font-semibold text-foreground">{formatCurrency(plannedMetrics.additionalAnnualIncome)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Capital net immobilisé</p>
                    <p className="text-lg font-semibold text-foreground">{formatCurrency(plannedMetrics.netCost)}</p>
                  </div>
                  {plannedMetrics.recoveryRate != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Taux de récupération</p>
                      <p className="text-lg font-semibold text-foreground">{(plannedMetrics.recoveryRate * 100).toFixed(0)}%</p>
                    </div>
                  )}
                  {plannedMetrics.realYield != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Rendement réel (revenus)</p>
                      <p className="text-2xl font-bold text-primary">{(plannedMetrics.realYield * 100).toFixed(1)}%</p>
                    </div>
                  )}
                  {plannedMetrics.paybackYears != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Délai récup. capital</p>
                      <p className="text-lg font-semibold text-foreground">{plannedMetrics.paybackYears.toFixed(1)} an{plannedMetrics.paybackYears > 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actual Cost (only show if completed or in progress) */}
            {(formData.status === 'completed' || formData.status === 'in_progress') && (
              <Input
                label="Coût réel"
                type="number"
                placeholder="Ex: 52000"
                value={formData.actual_cost}
                onChange={(e) => handleFieldChange('actual_cost', e.target.value)}
                min="0"
                description={formData.estimated_cost && formData.actual_cost ?
                  `Différence: ${formatCurrency(parseFloat(formData.actual_cost) - parseFloat(formData.estimated_cost))}` :
                  'Entrez le coût réel une fois le projet terminé'
                }
              />
            )}

            {/* Planning metrics - Actual (when completed) */}
            {actualMetrics && formData.status === 'completed' && projectFields.showValueIncrease && (actualMetrics.valueCreation > 0 || actualMetrics.realYield != null) && (
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3">Impact final du projet</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Valeur créée</p>
                    <p className="text-lg font-semibold text-success">{formatCurrency(actualMetrics.valueCreation)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Capital net immobilisé</p>
                    <p className="text-lg font-semibold text-foreground">{formatCurrency(actualMetrics.netCost)}</p>
                  </div>
                  {actualMetrics.recoveryRate != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Taux de récupération</p>
                      <p className="text-lg font-semibold text-foreground">{(actualMetrics.recoveryRate * 100).toFixed(0)}%</p>
                    </div>
                  )}
                  {actualMetrics.realYield != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Rendement réel</p>
                      <p className="text-2xl font-bold text-success">{(actualMetrics.realYield * 100).toFixed(1)}%</p>
                    </div>
                  )}
                  {actualMetrics.paybackYears != null && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Délai récup. capital</p>
                      <p className="text-lg font-semibold text-foreground">{actualMetrics.paybackYears.toFixed(1)} an{actualMetrics.paybackYears > 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="space-y-4 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Échéancier du projet</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Date de début"
                type="date"
                value={formData.planned_start_date}
                onChange={(e) => handleFieldChange('planned_start_date', e.target.value)}
                description="Date prévue de début des travaux"
              />

              <Input
                label="Date de fin"
                type="date"
                value={formData.planned_end_date}
                onChange={(e) => handleFieldChange('planned_end_date', e.target.value)}
                min={formData.planned_start_date || undefined}
                description="Date prévue de fin des travaux"
              />
            </div>

            {/* Actual dates (only if in progress or completed) */}
            {(formData.status === 'in_progress' || formData.status === 'completed') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <Input
                  label="Date de début réelle"
                  type="date"
                  value={formData.actual_start_date}
                  onChange={(e) => handleFieldChange('actual_start_date', e.target.value)}
                  description="Date réelle de début des travaux"
                />

                <Input
                  label="Date de fin réelle"
                  type="date"
                  value={formData.actual_end_date}
                  onChange={(e) => handleFieldChange('actual_end_date', e.target.value)}
                  min={formData.actual_start_date || undefined}
                  description="Date réelle de fin des travaux"
                />
              </div>
            )}

            {/* Completion notes (only if completed) */}
            {formData.status === 'completed' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notes de complétion
                </label>
                <textarea
                  className="w-full min-h-[100px] px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Notes sur le projet terminé..."
                  value={formData.completion_notes}
                  onChange={(e) => handleFieldChange('completion_notes', e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1"
            >
              {project ? 'Sauvegarder' : 'Créer le projet'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
